// src/utils/readerApi.js
import {
  API_BASE, getJSON, postJSON, patchJSON,
  tryRefreshAndRetry, clearSessionAndSignal,
} from './api';

/* ---------- shared tiny helpers ---------- */
// Bearer token, looked up the same way api.js's withAuth() does.
//
// No backend route accepts the X-User-ID header as proof of identity any more
// — it was spoofable, and comments.py, notes.py and HandWritingNotes.py have
// each had the fallback removed. Every helper here must therefore send a real
// token.
//
// X-User-ID used to ride along here as a request annotation. It has been
// dropped, because it was silently breaking every write this file makes: the
// API's CORS policy allows Content-Type, Authorization and X-RV-Page, so a
// PUT or DELETE carrying an unlisted X-User-ID failed its preflight and never
// left the browser. Reads were unaffected — they go through api.js's
// withAuth(), which only ever sent allowed headers — which is why this looked
// like "saving is broken" rather than like a CORS problem. Nothing read the
// header: the only backend mentions are notes recording its removal and tests
// asserting it is ignored.
function authToken() {
  return (
    localStorage.getItem('rv_access_token') ||
    localStorage.getItem('access_token') ||
    ''
  );
}

function authHeaders(extra = {}) {
  const token = authToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * A 401 is a stale access token, not a failed write.
 *
 * Every helper in this file used to give up on the first 401, while api.js's
 * getJSON and postJSON refreshed the token and retried. An author whose token
 * had aged out could therefore still *create* a story-element relationship —
 * that POST recovered — and could not *delete* the same relationship a moment
 * later, because this DELETE did not. That asymmetry is the whole of the
 * "the X button does nothing" bug: the button and the route were always fine.
 *
 * Kept in one place rather than inlined at each call site, and deliberately the
 * same shape as api.js's helpers — refresh, retry once, and on an unrecoverable
 * refusal clear the session so the app redirects, instead of leaving the author
 * clicking a control whose writes quietly never land.
 */
async function sendWithRefresh(path, init) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, init);

  if (res.status === 401) {
    const retryRes = await tryRefreshAndRetry(url, init);
    if (retryRes && retryRes.ok) return retryRes.json();
    clearSessionAndSignal();
    return null;
  }
  // 403 is a permission decision, not an expired token — report it, keep the
  // session. Same rule getJSON follows.
  if (!res.ok) throw new Error((await res.text()) || res.statusText);
  return res.json();
}

async function putJSON(path, body) {
  return sendWithRefresh(path, {
    method: 'PUT',
    credentials: 'include',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body ?? {}),
  });
}

async function delJSON(path) {
  return sendWithRefresh(path, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders(),
  });
}

/* ---------- Comments API ---------- */
export const commentApi = {
  async list({ bookId, type = 'book', page = null, limit = 50, before = null }) {
    if (!bookId) throw new Error('bookId required');
    const qs = new URLSearchParams({ type, limit: String(limit) });
    if (page !== null && page !== undefined) qs.set('page', String(page));
    if (before) qs.set('before', String(before));
    return getJSON(`/api/books/${bookId}/comments?${qs.toString()}`);
  },

  async create({
    bookId, type = 'book', text, page = null, highlight = null, quote = null,
    blockId = null, chapterId = null, lineHint = null, tags = null,
  }) {
    if (!bookId) throw new Error('bookId required');
    const body = { text: String(text || '').trim() };
    if (!body.text) throw new Error('Comment text required');

    if (page !== null && page !== undefined) body.page = page;
    if (highlight) body.highlight = highlight;   // { page, rects[] } from picker
    if (quote) body.quote = String(quote).trim();

    // Collab-panel anchors — see comments.py create_comment for their meaning.
    if (blockId) body.blockId = blockId;
    if (chapterId) body.chapterId = chapterId;
    if (lineHint !== null && lineHint !== undefined) body.lineHint = lineHint;
    if (Array.isArray(tags) && tags.length) body.tags = tags;

    const qs = new URLSearchParams({ type });
    return postJSON(`/api/books/${bookId}/comments?${qs.toString()}`, body);
  },

  async remove(commentId) {
    if (!commentId) throw new Error('commentId required');
    return delJSON(`/api/comments/${commentId}`);
  },

  /* ---- collab panel ---- */

  // Per-viewer read state (drives the dimmed chapter/line rows).
  async markRead(commentId) {
    if (!commentId) throw new Error('commentId required');
    return postJSON(`/api/comments/${commentId}/read`);
  },

  async markUnread(commentId) {
    if (!commentId) throw new Error('commentId required');
    return postJSON(`/api/comments/${commentId}/unread`);
  },

  // Edit text (owner only) and/or replace tags.
  async update(commentId, { text, tags } = {}) {
    if (!commentId) throw new Error('commentId required');
    const body = {};
    if (text !== undefined) body.text = text;
    if (tags !== undefined) body.tags = tags;
    if (!Object.keys(body).length) throw new Error('Nothing to update');
    return patchJSON(`/api/comments/${commentId}`, body);
  },

  // Returns { notified: [], skipped: [] }. `skipped` are users already
  // notified about this comment — they can only be notified once.
  async notify(commentId, userIds) {
    if (!commentId) throw new Error('commentId required');
    if (!Array.isArray(userIds) || !userIds.length) throw new Error('userIds required');
    return postJSON(`/api/comments/${commentId}/notify`, { userIds });
  },

  async discard(commentId) {
    if (!commentId) throw new Error('commentId required');
    return postJSON(`/api/comments/${commentId}/discard`);
  },

  async report(commentId, reason = '') {
    if (!commentId) throw new Error('commentId required');
    return postJSON(`/api/comments/${commentId}/report`, { reason });
  },
};

/* ---------- Language (spelling + grammar) API ---------- */
export const languageApi = {
  // Returns { matches: [{ offset, length, message, replacements[], ruleType, ... }], language }
  async check({ text, language = 'en-US' }) {
    return postJSON('/api/language/check', { text: String(text || ''), language });
  },
};

/* ---------- Citations API (per-draft sources) ---------- */
export const citationApi = {
  async list(draftId) {
    if (!draftId) throw new Error('draftId required');
    return getJSON(`/api/drafts/${draftId}/citations`);
  },
  async create(draftId, source) {
    if (!draftId) throw new Error('draftId required');
    return postJSON(`/api/drafts/${draftId}/citations`, source);
  },
  async update(draftId, citationId, source) {
    if (!draftId || !citationId) throw new Error('draftId + citationId required');
    return putJSON(`/api/drafts/${draftId}/citations/${citationId}`, source);
  },
  async remove(draftId, citationId) {
    if (!draftId || !citationId) throw new Error('draftId + citationId required');
    return delJSON(`/api/drafts/${draftId}/citations/${citationId}`);
  },
  // Auto-fill a source from a DOI or ISBN. Returns { source: {...} }.
  async lookup(query) {
    return postJSON('/api/citations/lookup', { query: String(query || '') });
  },
};

/* ---------- Story elements API (per-draft scenes, actors, artifacts) ---------- */
//
// The board behind the editor's Story Elements tab. Chapters are NOT here:
// they're derived from the manuscript DOM by src/utils/outline.js, and a
// relationship reaches one through its heading's block id. An edge endpoint is
// a reference string — `node:<id>` or `chapter:<blockId>` — which is why
// `ref` exists below rather than every caller hand-building the prefix.
export const storyElementApi = {
  // Reference strings for an edge's `from` / `to`.
  ref: {
    node: (nodeId) => `node:${nodeId}`,
    chapter: (blockId) => `chapter:${blockId}`,
  },

  // -> { storyElements: { version, nodes[], edges[], updatedAt } }
  // A draft that has never had an element returns an empty board, not a 404.
  async get(draftId) {
    if (!draftId) throw new Error('draftId required');
    return getJSON(`/api/drafts/${draftId}/story-elements`);
  },

  // x/y are optional: the Story Elements tab creates elements without a
  // position, the Cork Board creates them with one.
  async createNode(draftId, { type, name, desc = '', x, y }) {
    if (!draftId) throw new Error('draftId required');
    const body = { type, name, desc };
    if (Number.isFinite(x) && Number.isFinite(y)) { body.x = x; body.y = y; }
    return postJSON(`/api/drafts/${draftId}/story-elements/nodes`, body);
  },

  // Send only the fields being changed — the panel autosaves one at a time.
  async updateNode(draftId, nodeId, fields) {
    if (!draftId || !nodeId) throw new Error('draftId + nodeId required');
    return putJSON(`/api/drafts/${draftId}/story-elements/nodes/${nodeId}`, fields);
  },

  // Move a card on the Cork Board. Its own endpoint, not updateNode: call it
  // on drop, never on every pointermove — each call broadcasts to collaborators.
  // -> { _id, x, y }
  async moveNode(draftId, nodeId, { x, y }) {
    if (!draftId || !nodeId) throw new Error('draftId + nodeId required');
    return putJSON(
      `/api/drafts/${draftId}/story-elements/nodes/${nodeId}/position`,
      { x, y },
    );
  },

  // Removes the element and every relationship touching it, server-side.
  async removeNode(draftId, nodeId) {
    if (!draftId || !nodeId) throw new Error('draftId + nodeId required');
    return delJSON(`/api/drafts/${draftId}/story-elements/nodes/${nodeId}`);
  },

  async createEdge(draftId, { from, to, label = '' }) {
    if (!draftId) throw new Error('draftId required');
    return postJSON(`/api/drafts/${draftId}/story-elements/edges`, { from, to, label });
  },

  async updateEdge(draftId, edgeId, label) {
    if (!draftId || !edgeId) throw new Error('draftId + edgeId required');
    return putJSON(`/api/drafts/${draftId}/story-elements/edges/${edgeId}`, { label });
  },

  async removeEdge(draftId, edgeId) {
    if (!draftId || !edgeId) throw new Error('draftId + edgeId required');
    return delJSON(`/api/drafts/${draftId}/story-elements/edges/${edgeId}`);
  },
};

/* ---------- Notes API (per-user per-page) ---------- */
export const notesApi = {
  async getPage({ bookId, page }) {
    if (!bookId && bookId !== 0) throw new Error('bookId required');
    if (page === undefined || page === null) throw new Error('page required');
    return getJSON(`/api/books/${bookId}/notes/pages/${page}`);
  },

  async savePage({ bookId, page, fabricJson }) {
    if (!bookId && bookId !== 0) throw new Error('bookId required');
    if (page === undefined || page === null) throw new Error('page required');
    return putJSON(`/api/books/${bookId}/notes/pages/${page}`, { fabricJson });
  },

  async summary({ bookId }) {
    if (!bookId && bookId !== 0) throw new Error('bookId required');
    return getJSON(`/api/books/${bookId}/notes/summary`);
  },
  // List note items (optionally filter by page)
  async list({ bookId, page } = {}) {
    if (!bookId) throw new Error('bookId required');
    const qs = page != null ? `?page=${encodeURIComponent(page)}` : '';
    return getJSON(`/api/books/${bookId}/note-items${qs}`); // -> { notes: [...] }
  },

  // Create a note item (free-form or with quote/highlight)
  async create({ bookId, text, quote, page, rects }) {
    if (!bookId) throw new Error('bookId required');
    if (!String(text || '').trim()) throw new Error('text required');
    return postJSON(`/api/books/${bookId}/note-items`, { text, quote, page, rects });
  },

  // Delete a note item. Uses delJSON so the request carries the bearer token —
  // it used to be a bare fetch with only `credentials: 'include'`, which sends
  // no identity the backend reads, so it answered 401.
  async remove({ bookId, noteId }) {
    if (!bookId) throw new Error('bookId required');
    if (!noteId) throw new Error('noteId required');
    return delJSON(
      `/api/books/${encodeURIComponent(bookId)}/note-items/${encodeURIComponent(noteId)}`
    ); // { ok: true }
  },
};

/* ---------- Handwriting API (per-user, per-page; vector JSON + meta) ---------- */
// These mirror the new Flask routes in routes/HandWritingNotes.py
// GET /api/books/:bookId/handwriting/pages/:page
// PUT /api/books/:bookId/handwriting/pages/:page
// GET /api/books/:bookId/handwriting/summary
//
// We store Fabric vector data (fabricJson) and minimal meta:
//   meta: { canvasWidth, canvasHeight, baseScale? }
// Use getJSON/putJSON already available in this module.

export const handwritingApi = {
  async getPage({ bookId, page }) {
    if (!bookId && bookId !== 0) throw new Error('bookId required');
    if (page === undefined || page === null) throw new Error('page required');
    return getJSON(`/api/books/${encodeURIComponent(bookId)}/handwriting/pages/${encodeURIComponent(page)}`);
  },

  async savePage({ bookId, page, fabricJson, meta }) {
    if (!bookId && bookId !== 0) throw new Error('bookId required');
    if (page === undefined || page === null) throw new Error('page required');
    if (!fabricJson || typeof fabricJson !== 'object') throw new Error('fabricJson object required');

    // meta should include the canvas dimensions used at draw-time for normalization later
    const safeMeta = meta && typeof meta === 'object' ? meta : {};
    if (typeof safeMeta.canvasWidth !== 'number' || typeof safeMeta.canvasHeight !== 'number') {
      // You can relax this if you want; keeping strict helps correctness.
      throw new Error('meta.canvasWidth and meta.canvasHeight required (numbers)');
    }

    return putJSON(
      `/api/books/${encodeURIComponent(bookId)}/handwriting/pages/${encodeURIComponent(page)}`,
      { fabricJson, meta: safeMeta }
    );
  },

  async summary({ bookId }) {
    if (!bookId && bookId !== 0) throw new Error('bookId required');
    return getJSON(`/api/books/${encodeURIComponent(bookId)}/handwriting/summary`);
  },
};
